import { useLocation } from "react-router-dom";
import { useEffect } from "react";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-100 to-blue-100 animate-fade-in">
      <div className="text-center">
        <div className="flex justify-center mb-6">
          {/* Interactive Cricket SVG Illustration */}
          <svg
            width="320"
            height="220"
            viewBox="0 0 320 220"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Cricket themed interactive illustration"
          >
            {/* Ground shadow */}
            <ellipse cx="160" cy="200" rx="90" ry="15" fill="#B2DFDB" opacity="0.3" className="animate-ground" />
            {/* Stumps */}
            <g className="stumps-group animate-stumps">
              <rect x="90" y="110" width="8" height="55" rx="4" fill="#BCA16B" />
              <rect x="110" y="110" width="8" height="55" rx="4" fill="#BCA16B" />
              <rect x="130" y="110" width="8" height="55" rx="4" fill="#BCA16B" />
              {/* Bails */}
              <rect x="90" y="106" width="20" height="5" rx="2.5" fill="#EAD18A" className="animate-bail-left" />
              <rect x="110" y="106" width="20" height="5" rx="2.5" fill="#EAD18A" className="animate-bail-right" />
            </g>
            {/* Ball (bouncing and hitting stumps) */}
            <circle cx="220" cy="120" r="11" fill="#E53E3E" className="animate-ball-404" />
            {/* Batsman (simple cartoon) */}
            <g className="batsman">
              {/* Head */}
              <circle cx="220" cy="150" r="14" fill="#FCD7B6" stroke="#E0A96D" strokeWidth="2" />
              {/* Body */}
              <rect x="212" y="164" width="16" height="30" rx="8" fill="#4F8A8B" />
              {/* Left leg */}
              <rect x="212" y="194" width="6" height="18" rx="3" fill="#FCD7B6" transform="rotate(10 212 194)" />
              {/* Right leg */}
              <rect x="222" y="194" width="6" height="18" rx="3" fill="#FCD7B6" transform="rotate(-10 222 194)" />
              {/* Left arm */}
              <rect x="200" y="170" width="16" height="5" rx="2.5" fill="#FCD7B6" transform="rotate(-20 200 170)" />
              {/* Right arm (bat holding) */}
              <rect x="228" y="170" width="16" height="5" rx="2.5" fill="#FCD7B6" transform="rotate(30 228 170)" />
              {/* Bat (animated swing) */}
              <rect x="242" y="160" width="10" height="50" rx="5" fill="#E0A96D" className="origin-bat animate-bat-swing" />
              <rect x="246" y="208" width="10" height="10" rx="4" fill="#A67C52" />
            </g>
          </svg>
        </div>
        <h1 className="text-5xl font-extrabold text-cricket-green mb-2 drop-shadow">404</h1>
        <p className="text-2xl text-gray-700 mb-4 font-semibold">Howzzat! This page is out of the boundary.</p>
        <p className="text-md text-gray-500 mb-6">The page you are looking for doesn't exist or has been moved.</p>
        <a href="/" className="inline-block px-6 py-3 rounded-lg bg-cricket-green text-white font-bold shadow hover:bg-green-700 transition-colors duration-200">
          Return to Home
        </a>
      </div>
      {/* Animations */}
      <style>{`
        .animate-bat-swing {
          transform-box: fill-box;
          transform-origin: 5px 5px;
          animation: bat-swing-404 2.5s infinite cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes bat-swing-404 {
          0%, 100% { transform: rotate(-25deg); }
          10% { transform: rotate(-5deg); }
          20% { transform: rotate(-40deg); }
          30% { transform: rotate(-10deg); }
          40% { transform: rotate(-25deg); }
        }
        .animate-ball-404 {
          animation: ball-bounce-404 2.5s infinite cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes ball-bounce-404 {
          0% { transform: translate(0, 0); }
          10% { transform: translate(-40px, 30px); }
          20% { transform: translate(-70px, 60px); }
          30% { transform: translate(-90px, 80px); }
          40% { transform: translate(-110px, 90px); }
          50% { transform: translate(-120px, 100px) scale(1.1); }
          55% { transform: translate(-120px, 100px) scale(1.2); }
          60% { transform: translate(-120px, 100px) scale(1); }
          70% { transform: translate(-120px, 100px) scale(1); }
          100% { transform: translate(0, 0); }
        }
        .animate-bail-left {
          animation: bail-pop-left 2.5s infinite cubic-bezier(0.4,0,0.2,1);
        }
        .animate-bail-right {
          animation: bail-pop-right 2.5s infinite cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes bail-pop-left {
          0%, 49% { transform: translateY(0); }
          50% { transform: translateY(-12px) rotate(-20deg); }
          60% { transform: translateY(-8px) rotate(-10deg); }
          70%, 100% { transform: translateY(0); }
        }
        @keyframes bail-pop-right {
          0%, 49% { transform: translateY(0); }
          50% { transform: translateY(-12px) rotate(20deg); }
          60% { transform: translateY(-8px) rotate(10deg); }
          70%, 100% { transform: translateY(0); }
        }
        .animate-stumps {
          animation: stumps-shake 2.5s infinite cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes stumps-shake {
          0%, 49% { transform: translateX(0); }
          50% { transform: translateX(-2px); }
          52% { transform: translateX(2px); }
          54% { transform: translateX(-1px); }
          56% { transform: translateX(1px); }
          58%, 100% { transform: translateX(0); }
        }
        .animate-ground {
          animation: ground-shadow 2.5s infinite cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes ground-shadow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.5; }
        }
        .text-cricket-green {
          color: #16a34a;
        }
        .bg-cricket-green {
          background: #16a34a;
        }
      `}</style>
    </div>
  );
};

export default NotFound;
